"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";

function normalizeCity(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i");
}

export default function CityCombobox({ cities, value, onChange }: { cities: string[]; value: string; onChange: (city: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const normalizedQuery = normalizeCity(query);
  const isCurrentValue = normalizedQuery === normalizeCity(value);
  const matches = useMemo(() => {
    if (!normalizedQuery || isCurrentValue) return cities;
    return cities
      .filter((city) => normalizeCity(city).includes(normalizedQuery))
      .sort((left, right) => {
        const leftStarts = normalizeCity(left).startsWith(normalizedQuery);
        const rightStarts = normalizeCity(right).startsWith(normalizedQuery);
        if (leftStarts !== rightStarts) return leftStarts ? -1 : 1;
        return left.localeCompare(right, "tr");
      });
  }, [cities, isCurrentValue, normalizedQuery]);

  useEffect(() => setQuery(value), [value]);

  function select(city: string) {
    setQuery(city);
    setOpen(false);
    setActiveIndex(0);
    if (city !== value) onChange(city);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.min(current + (open ? 1 : 0), Math.max(matches.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter" && open && matches[activeIndex]) {
      event.preventDefault();
      select(matches[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setQuery(value);
      setOpen(false);
    }
  }

  return <div className="relative min-w-0">
    <label htmlFor={`${listboxId}-input`} className="sr-only">Şehir ara ve seç</label>
    <div className="flex min-h-12 items-center gap-2 border border-[#a9b6ae] bg-white px-3 transition-colors focus-within:border-[#101412] focus-within:ring-1 focus-within:ring-[#101412]">
      <Search className="size-4 shrink-0 text-[#68736d]" aria-hidden />
      <input
        ref={inputRef}
        id={`${listboxId}-input`}
        type="search"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open && matches[activeIndex] ? `${listboxId}-option-${activeIndex}` : undefined}
        autoComplete="off"
        spellCheck={false}
        value={query}
        placeholder="İl ara..."
        onFocus={(event) => {
          setOpen(true);
          setActiveIndex(0);
          event.currentTarget.select();
        }}
        onBlur={() => {
          setOpen(false);
          setQuery(value);
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onKeyDown={handleKeyDown}
        className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[#101412] outline-none placeholder:font-normal placeholder:text-[#7b857f] [&::-webkit-search-cancel-button]:hidden"
      />
      <ChevronDown className={`size-4 shrink-0 text-[#68736d] transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
    </div>

    {open ? <div id={listboxId} role="listbox" aria-label="Eşleşen şehirler" className="absolute inset-x-0 top-[calc(100%+6px)] z-50 max-h-72 overflow-y-auto border border-[#a9b6ae] bg-white py-1 shadow-[0_18px_45px_rgba(16,20,18,.16)]">
      {matches.length ? matches.map((city, index) => <div
        key={city}
        id={`${listboxId}-option-${index}`}
        role="option"
        aria-selected={city === value}
        onMouseDown={(event) => {
          event.preventDefault();
          select(city);
          inputRef.current?.focus();
        }}
        onMouseEnter={() => setActiveIndex(index)}
        className={`flex min-h-10 cursor-pointer items-center justify-between gap-3 px-3 text-sm ${index === activeIndex ? "bg-[#f0f3f1] text-[#101412]" : "text-[#4f5a54]"}`}
      >
        <span className="font-semibold">{city}</span>
        {city === value ? <Check className="size-4 text-[#e63228]" aria-hidden /> : null}
      </div>) : <p className="px-3 py-4 text-sm text-[#68736d]">Bu aramayla eşleşen il bulunamadı.</p>}
    </div> : null}
    <span className="sr-only" aria-live="polite">{open ? `${matches.length} il eşleşti` : ""}</span>
  </div>;
}
